/**
 * Admin › AI Quality & Observability (read-only beta trust cockpit).
 *
 * A single in-app view of AI evals, usage, cost, retrieval evidence, and beta
 * certification status — figure-first (KPI tiles, trend + breakdown charts) rather
 * than a wall of text. Purpose-built for trust signals, NOT a generic analytics
 * dashboard. Shows only safe values — IDs, counts, bands, costs, timestamps — never
 * raw emails, document text, prompts, or LLM output.
 *
 * Data + source honesty live in @/lib/admin/use-ai-quality (pure summarizers, tested).
 * In mock/demo mode the hooks return sample data so the figures look alive; production
 * reads the real tables or shows an honest empty/unavailable state. Access is enforced
 * by (admin)/_layout.tsx — no per-page role check needed.
 */

import React, { useMemo, useState, useRef, useCallback } from 'react';
import { View, Text, ScrollView, ActivityIndicator, Pressable } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Animated, { FadeInDown } from 'react-native-reanimated';
import * as Clipboard from 'expo-clipboard';
import {
  Gauge,
  Activity,
  Coins,
  FlaskConical,
  Search,
  ShieldCheck,
  Bell,
  Terminal,
  Copy,
  Check,
  FileText,
  CircleDot,
  DollarSign,
  Cpu,
  AlertTriangle,
  Timer,
  CheckCircle2,
  Workflow,
} from 'lucide-react-native';
import { AppLayout } from '@/components/AppLayout';
import { useThemeColors } from '@/lib/useThemeColors';
import { formatCompact } from '@/lib/utils';
import { isWeb } from '@/lib/platform';
import { WebCard, WebGrid } from '@/components/web/WebAppShell';
import { StatCardEnhanced } from '@/components/admin/StatCardEnhanced';
import { AreaChart } from '@/components/admin/AreaChart';
import { MiniBarChart } from '@/components/admin/MiniBarChart';
import { AdoptionBar } from '@/components/admin/SortableTable';
import { ObservabilityStatusChip, type ObservabilityStatus } from '@/components/admin/ObservabilityStatusChip';
import {
  summarizeHealthToday,
  recomputeAlerts,
  summarizeProofEvents,
  recentFlowEvents,
  latestRunPerSuite,
  EVAL_SUITE_LABEL,
  useHealthUsageRows,
  useLatestScoringEvals,
  useRecentRagRetrieval,
  useObservabilityEvents,
  useEvalRuns,
  type RetrievalStatus,
} from '@/lib/admin/use-ai-quality';
import { APP_LOCALE } from '@/lib/format';

const fmtUsd = (n: number) => `$${n.toFixed(n < 1 ? 4 : 2)}`;

/** Strip the provider/`-latest` noise so a model reads as a short bar label. */
function shortModel(model: string): string {
  return model.replace(/-latest$/, '').replace(/^(mistral|openai|anthropic|claude)-/, '');
}

function timeAgo(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '—';
  const mins = Math.floor((Date.now() - then) / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(APP_LOCALE);
}

// --- Static references (safe, no fabricated data) -------------------------------

// `source`: 'table' = persisted + queryable in ai_observability_events (real counts);
// 'analytics' = analytics_events; 'log' = Supabase logs only (not persisted yet).
const PROOF_EVENTS: { event: string; where: string; source: 'table' | 'analytics' | 'log' }[] = [
  { event: 'processing_complete', where: 'ai_observability_events · index-document', source: 'table' },
  { event: 'embedding_reuse_summary', where: 'ai_observability_events · index-document', source: 'table' },
  { event: 'dd_rag_request', where: 'ai_observability_events · dd-chat-rag', source: 'table' },
  { event: 'submission_info_requested', where: 'ai_observability_events · submission-action', source: 'table' },
  { event: 'submission_claim_invited', where: 'ai_observability_events · submission-action', source: 'table' },
  { event: 'submission_claimed', where: 'ai_observability_events · claim-submission', source: 'table' },
  { event: 'score_persisted', where: 'ai_observability_events · score-and-persist', source: 'table' },
  { event: 'dd_rag_retrieval', where: 'analytics_events (see Retrieval Evidence) + client', source: 'analytics' },
];

const COMMANDS: { label: string; cmd: string }[] = [
  { label: 'Certification proof-event guard', cmd: 'bun run guard:cert' },
  {
    label: 'Focused Deno eval suite (offline, CI)',
    cmd: 'deno test --allow-net --allow-env --allow-read --no-check --config supabase/deno.json supabase/functions/_shared/evals/',
  },
  {
    label: 'Live scoring eval (persists to scoring_evals)',
    cmd: 'deno run --env-file=.env --allow-read --allow-env --allow-net supabase/functions/_shared/evals/scoring/live/run-live.ts',
  },
  {
    label: 'Live RAG faithfulness eval',
    cmd: 'deno run --env-file=.env --allow-read --allow-env --allow-net supabase/functions/_shared/evals/rag/run-rag-live.ts --persist',
  },
  {
    label: 'Live RAG retrieval eval (needs seeded startup)',
    cmd: 'deno run --env-file=.env --allow-read --allow-env --allow-net supabase/functions/_shared/evals/rag/retrieval/run-retrieval-live.ts --persist',
  },
  {
    label: 'Live extraction eval (recall + precision)',
    cmd: 'deno run --env-file=.env --allow-read --allow-env --allow-net supabase/functions/_shared/evals/extraction/run-extraction-live.ts --persist',
  },
];

// --- Small building blocks ------------------------------------------------------

function SectionHeaderRow({
  icon,
  title,
  source,
  right,
}: {
  icon: React.ReactNode;
  title: string;
  source: string;
  right?: React.ReactNode;
}) {
  const colors = useThemeColors();
  return (
    <View className="flex-row items-start justify-between mt-6 mb-2">
      <View className="flex-1 pr-2">
        <View className="flex-row items-center gap-2">
          {icon}
          <Text style={{ color: colors.text.primary }} className="text-base font-semibold" numberOfLines={1}>
            {title}
          </Text>
        </View>
        <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={2}>
          {source}
        </Text>
      </View>
      {right}
    </View>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  const colors = useThemeColors();
  return (
    <View className="rounded-2xl border overflow-hidden" style={{ borderColor: colors.border.light }}>
      {children}
    </View>
  );
}

function Row({ children, first }: { children: React.ReactNode; first?: boolean }) {
  const colors = useThemeColors();
  return (
    <View
      className="px-3 py-2.5"
      style={{
        backgroundColor: colors.surface.primary,
        borderTopWidth: first ? 0 : 1,
        borderTopColor: colors.border.light,
      }}
    >
      {children}
    </View>
  );
}

function NoteCard({ children }: { children: React.ReactNode }) {
  const colors = useThemeColors();
  return (
    <View
      className="rounded-2xl border p-3"
      style={{ backgroundColor: colors.surface.primary, borderColor: colors.border.light }}
    >
      {children}
    </View>
  );
}

/** A labeled chart card with an optional headline figure on the right. */
function ChartCard({ title, subtitle, headline, children }: { title: string; subtitle?: string; headline?: React.ReactNode; children: React.ReactNode }) {
  const colors = useThemeColors();
  return (
    <WebCard>
      <View className="flex-row items-start justify-between mb-3">
        <View className="flex-1 pr-2">
          <Text style={{ color: colors.text.primary }} className="text-sm font-semibold" numberOfLines={1}>{title}</Text>
          {subtitle ? <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={1}>{subtitle}</Text> : null}
        </View>
        {headline}
      </View>
      {children}
    </WebCard>
  );
}

function CopyCommand({ label, cmd }: { label: string; cmd: string }) {
  const colors = useThemeColors();
  const [copied, setCopied] = useState(false);
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onCopy = useCallback(async () => {
    await Clipboard.setStringAsync(cmd);
    setCopied(true);
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = setTimeout(() => setCopied(false), 2200);
  }, [cmd]);

  return (
    <Row>
      <View className="flex-row items-center justify-between gap-2">
        <Text style={{ color: colors.text.secondary }} className="text-xs flex-1" numberOfLines={2}>
          {label}
        </Text>
        <Pressable
          onPress={onCopy}
          accessibilityRole="button"
          accessibilityLabel={copied ? 'Copied' : `Copy: ${label}`}
          className="flex-row items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{ backgroundColor: colors.surface.secondary, borderWidth: 1, borderColor: colors.border.light }}
        >
          {copied ? <Check size={13} color={colors.success} /> : <Copy size={13} color={colors.text.tertiary} />}
          <Text style={{ color: copied ? colors.success : colors.text.tertiary }} className="text-xs font-medium">
            {copied ? 'Copied' : 'Copy'}
          </Text>
        </Pressable>
      </View>
      <Text
        style={{ color: colors.text.tertiary, fontFamily: 'monospace' }}
        className="text-xs mt-1.5"
        numberOfLines={2}
      >
        {cmd}
      </Text>
    </Row>
  );
}

const retrievalChip: Record<RetrievalStatus, { status: ObservabilityStatus; label: string }> = {
  healthy: { status: 'healthy', label: 'OK' },
  'warning-empty': { status: 'warning', label: '0 chunks' },
  'warning-low': { status: 'warning', label: 'Low sim.' },
};

export default function AIQualityScreen() {
  const colors = useThemeColors();
  const nowMs = Date.now();

  const { data: usageRows, isLoading: usageLoading, isError: usageError } = useHealthUsageRows(30);
  const health = useMemo(() => summarizeHealthToday(usageRows ?? [], nowMs), [usageRows, nowMs]);
  const alertState = useMemo(() => recomputeAlerts(usageRows ?? [], nowMs), [usageRows, nowMs]);

  const { data: scoringRun, isLoading: scoringLoading, isError: scoringError } = useLatestScoringEvals();
  const { data: retrieval, isLoading: retrievalLoading, isError: retrievalError } = useRecentRagRetrieval(25);
  const { data: obsEvents, isLoading: obsLoading } = useObservabilityEvents(500);
  const { data: evalRuns } = useEvalRuns(40);

  const proofStats = useMemo(() => summarizeProofEvents(obsEvents ?? []), [obsEvents]);
  const flowEvents = useMemo(() => recentFlowEvents(obsEvents ?? [], 12), [obsEvents]);
  const latestEvalRuns = useMemo(() => latestRunPerSuite(evalRuns ?? []), [evalRuns]);

  // Derived figures ------------------------------------------------------------
  const costTrend = useMemo(
    () => health.trend.map((t) => ({ date: t.day.slice(5), values: [Number(t.costUsd.toFixed(4))] })),
    [health.trend],
  );
  const modelBars = useMemo(
    () => health.breakdown.slice(0, 6).map((m) => ({ label: shortModel(m.model), value: Number(m.costUsd.toFixed(4)) })),
    [health.breakdown],
  );
  const errTint = health.today.errorRate > 0.05 ? colors.traffic.red : colors.primary[600];

  const scorePct = scoringRun && scoringRun.total > 0 ? Math.round((scoringRun.passCount / scoringRun.total) * 100) : 0;
  const scoreBars = useMemo(
    () => (scoringRun?.cases ?? []).map((c) => ({ label: c.caseName.slice(0, 2), value: c.finalScore })),
    [scoringRun],
  );

  const retrievalStats = useMemo(() => {
    const events = retrieval?.events ?? [];
    if (events.length === 0) return null;
    const avgSim = events.reduce((s, e) => s + e.topSimilarity, 0) / events.length;
    const healthy = events.filter((e) => e.status === 'healthy').length;
    return { avgSim, healthy, total: events.length, healthyPct: Math.round((healthy / events.length) * 100) };
  }, [retrieval]);
  const retrievalBars = useMemo(
    () => (retrieval?.events ?? []).slice(0, 10).map((e, i) => ({ label: `${i + 1}`, value: Math.round(e.topSimilarity * 100) })),
    [retrieval],
  );

  return (
    <AppLayout
      title="AI Quality"
      breadcrumbs={[{ label: 'Admin', href: '/(admin)/admin-dashboard' }, { label: 'AI Quality' }]}
    >
      <SafeAreaView className="flex-1" style={{ backgroundColor: colors.surface.secondary }} edges={isWeb ? [] : ['top', 'bottom']}>
        <ScrollView showsVerticalScrollIndicator={false} contentContainerStyle={{ paddingBottom: 40, paddingHorizontal: 2 }}>
          {/* Page header */}
          <View className="pt-2 pb-1">
            <View className="flex-row items-center gap-2">
              <Gauge size={18} color={colors.text.primary} />
              <Text style={{ color: colors.text.primary }} className="text-xl font-bold">
                AI Quality &amp; Observability
              </Text>
            </View>
            <Text style={{ color: colors.text.secondary }} className="text-sm mt-1">
              Read-only beta trust cockpit. Each card names its source; values are safe IDs, counts,
              bands, costs, and timestamps only.
            </Text>
          </View>

          {/* 1 — AI Health Today */}
          <Animated.View entering={FadeInDown.duration(400)}>
            <SectionHeaderRow
              icon={<Activity size={16} color={colors.primary[600]} />}
              title="AI Health Today"
              source="Source: llm_usage_daily (live · per-call LLM rollup). Cost is an estimate."
            />
            {usageLoading ? (
              <ActivityIndicator color={colors.primary[600]} />
            ) : usageError ? (
              <Text style={{ color: colors.traffic.red }} className="text-sm">Couldn’t load llm_usage_daily.</Text>
            ) : !health.hasAnyData ? (
              <NoteCard>
                <Text style={{ color: colors.text.tertiary }} className="text-sm">
                  No LLM usage recorded in the last 30 days. Rows appear as production model calls run.
                </Text>
              </NoteCard>
            ) : (
              <>
                <WebGrid columns={{ mobile: 2, tablet: 4, desktop: 4 }} gap={12}>
                  <StatCardEnhanced
                    icon={<Cpu size={20} color={colors.primary[600]} />}
                    iconBg={`${colors.primary[600]}14`}
                    label="Calls today"
                    value={health.today.calls.toLocaleString()}
                  />
                  <StatCardEnhanced
                    icon={<DollarSign size={20} color={colors.primary[600]} />}
                    iconBg={`${colors.primary[600]}14`}
                    label="Est. cost today"
                    value={fmtUsd(health.today.costUsd)}
                  />
                  <StatCardEnhanced
                    icon={<AlertTriangle size={20} color={errTint} />}
                    iconBg={`${errTint}14`}
                    label="Error rate today"
                    value={`${(health.today.errorRate * 100).toFixed(1)}%`}
                  />
                  <StatCardEnhanced
                    icon={<Timer size={20} color={colors.primary[600]} />}
                    iconBg={`${colors.primary[600]}14`}
                    label="Avg latency"
                    value={`${Math.round(health.today.avgLatencyMs)}ms`}
                  />
                </WebGrid>

                <Text style={{ color: colors.text.tertiary }} className="text-xs mt-2 px-1">
                  Yesterday: {health.yesterday.calls.toLocaleString()} calls · {fmtUsd(health.yesterday.costUsd)} ·{' '}
                  {(health.yesterday.errorRate * 100).toFixed(1)}% err · {formatCompact(health.today.inputTokens + health.today.outputTokens)} tokens today
                </Text>

                <View className="mt-3">
                  <ChartCard title="AI spend" subtitle="Estimated daily cost · last 30 days">
                    {costTrend.length >= 2 ? (
                      <AreaChart data={costTrend} colors={[colors.primary[500]]} labels={['Cost $']} height={isWeb ? 200 : 160} />
                    ) : (
                      <Text style={{ color: colors.text.tertiary }} className="text-xs py-8 text-center">Not enough days to chart yet.</Text>
                    )}
                  </ChartCard>
                </View>

                {modelBars.length > 0 && (
                  <View className="mt-3">
                    <ChartCard title="Cost by model" subtitle={`Provider/model spend · ${health.breakdownDay}`}>
                      <MiniBarChart data={modelBars} color={colors.primary[600]} height={isWeb ? 120 : 100} />
                    </ChartCard>
                  </View>
                )}
              </>
            )}
          </Animated.View>

          {/* 2 — Latest Eval Runs */}
          <Animated.View entering={FadeInDown.duration(400)}>
            <SectionHeaderRow
              icon={<FlaskConical size={16} color={colors.primary[600]} />}
              title="Latest Eval Runs"
              source="Source: scoring_evals (live scoring runner). RAG & extraction live evals are operator-run and not persisted."
            />
            {scoringLoading ? (
              <ActivityIndicator color={colors.primary[600]} />
            ) : scoringError ? (
              <Text style={{ color: colors.traffic.red }} className="text-sm">Couldn’t load scoring_evals.</Text>
            ) : !scoringRun ? (
              <NoteCard>
                <View className="flex-row items-center gap-2 mb-1">
                  <ObservabilityStatusChip status="operator-only" label="Not persisted yet" />
                </View>
                <Text style={{ color: colors.text.tertiary }} className="text-sm">
                  No scoring eval run has persisted to scoring_evals. Run the live scoring eval
                  (see “How to verify”) to populate this.
                </Text>
              </NoteCard>
            ) : (
              <ChartCard
                title={`Run ${scoringRun.runId}`}
                subtitle={`methodology ${scoringRun.methodologyVersion} · ${timeAgo(scoringRun.createdAt)}`}
                headline={
                  <View className="items-end">
                    <Text style={{ color: scoringRun.failCount === 0 ? colors.success : colors.warning }} className="text-2xl font-bold">
                      {scoringRun.passCount}/{scoringRun.total}
                    </Text>
                    <Text style={{ color: colors.text.tertiary }} className="text-xs">in band</Text>
                  </View>
                }
              >
                <View className="mb-3">
                  <AdoptionBar value={scorePct} color={scoringRun.failCount === 0 ? colors.success : colors.warning} />
                </View>
                <Text style={{ color: colors.text.tertiary }} className="text-xs mb-1">Final score by case</Text>
                <MiniBarChart data={scoreBars} color={colors.primary[600]} height={isWeb ? 110 : 90} />
                <View className="mt-3">
                  {scoringRun.cases.map((c, i) => (
                    <View
                      key={c.caseName}
                      className="flex-row items-center justify-between py-1.5"
                      style={{ borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.border.light }}
                    >
                      <View className="flex-1 pr-2">
                        <Text style={{ color: colors.text.primary }} className="text-xs" numberOfLines={1}>{c.caseName}</Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                          expected {c.expectedBand} · actual {c.actualBand} · score {c.finalScore} · spread {c.spread}
                        </Text>
                      </View>
                      <ObservabilityStatusChip status={c.inBand ? 'healthy' : 'missing'} label={c.inBand ? 'In band' : 'Out'} />
                    </View>
                  ))}
                </View>
              </ChartCard>
            )}
            <Text style={{ color: colors.text.tertiary }} className="text-xs mt-3 mb-1 px-1">
              Live eval history · RAG + extraction (ai_eval_runs · operator-run with --persist)
            </Text>
            {latestEvalRuns.length === 0 ? (
              <NoteCard>
                <View className="flex-row items-center gap-2 mb-1">
                  <ObservabilityStatusChip status="operator-only" label="Operator-run" />
                </View>
                <Text style={{ color: colors.text.tertiary }} className="text-sm">
                  No RAG/extraction eval runs persisted yet. Run a live eval with{' '}
                  <Text style={{ fontFamily: 'monospace' }}>--persist</Text> (see “How to verify”) to populate history.
                </Text>
              </NoteCard>
            ) : (
              <Panel>
                {latestEvalRuns.map((run, i) => {
                  const pct = run.total > 0 ? Math.round((run.passed / run.total) * 100) : 0;
                  return (
                    <Row key={run.suite} first={i === 0}>
                      <View className="flex-row items-center justify-between gap-2">
                        <View className="flex-1">
                          <Text style={{ color: colors.text.primary }} className="text-sm" numberOfLines={1}>
                            {EVAL_SUITE_LABEL[run.suite] ?? run.suite}
                          </Text>
                          <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                            {run.passed}/{run.total} passed · {pct}% · {timeAgo(run.created_at)}
                          </Text>
                        </View>
                        <ObservabilityStatusChip status={run.failed === 0 ? 'healthy' : 'warning'} label={`${run.passed}/${run.total}`} />
                      </View>
                    </Row>
                  );
                })}
              </Panel>
            )}
          </Animated.View>

          {/* 3 — RAG / DD Retrieval Evidence */}
          <Animated.View entering={FadeInDown.duration(400)}>
            <SectionHeaderRow
              icon={<Search size={16} color={colors.primary[600]} />}
              title="RAG / DD Retrieval Evidence"
              source="Source: analytics_events · dd_rag_retrieval (requires super_admin/analyst role to read)."
            />
            {retrievalLoading ? (
              <ActivityIndicator color={colors.primary[600]} />
            ) : retrievalError ? (
              <Text style={{ color: colors.traffic.red }} className="text-sm">Couldn’t load analytics_events.</Text>
            ) : !retrieval || retrieval.empty ? (
              <NoteCard>
                <Text style={{ color: colors.text.tertiary }} className="text-sm">
                  No dd_rag_retrieval events returned. Either none have been recorded yet, or your admin
                  role can’t read analytics_events (super_admin/analyst only).
                </Text>
              </NoteCard>
            ) : (
              <ChartCard
                title="Recent retrievals"
                subtitle={`${retrievalStats?.total ?? 0} events · top-similarity per query`}
                headline={
                  retrievalStats ? (
                    <View className="items-end">
                      <Text style={{ color: colors.text.primary }} className="text-2xl font-bold">{retrievalStats.healthyPct}%</Text>
                      <Text style={{ color: colors.text.tertiary }} className="text-xs">healthy · avg {retrievalStats.avgSim.toFixed(2)}</Text>
                    </View>
                  ) : undefined
                }
              >
                <MiniBarChart data={retrievalBars} color={colors.primary[600]} height={isWeb ? 110 : 90} />
                <View className="mt-3">
                  {retrieval.events.slice(0, 8).map((e, i) => {
                    const chip = retrievalChip[e.status];
                    return (
                      <View
                        key={`${e.createdAt}-${i}`}
                        className="flex-row items-center justify-between py-1.5"
                        style={{ borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.border.light }}
                      >
                        <View className="flex-1 pr-2">
                          <Text style={{ color: colors.text.primary }} className="text-xs" numberOfLines={1}>{e.startupId ?? 'unknown startup'}</Text>
                          <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>
                            {e.chunkCount} chunks · top sim {e.topSimilarity.toFixed(2)} · {timeAgo(e.createdAt)}
                          </Text>
                        </View>
                        <ObservabilityStatusChip status={chip.status} label={chip.label} />
                      </View>
                    );
                  })}
                </View>
              </ChartCard>
            )}
          </Animated.View>

          {/* 4 — DD Flow Evidence (real, from ai_observability_events) */}
          <Animated.View entering={FadeInDown.duration(400)}>
            <SectionHeaderRow
              icon={<Workflow size={16} color={colors.primary[600]} />}
              title="DD Flow Evidence"
              source="Source: ai_observability_events (live · PII-safe proof events). Recent activity across document, retrieval, and submission flows."
              right={
                <View className="items-end">
                  <Text style={{ color: colors.text.primary }} className="text-lg font-bold">{(obsEvents ?? []).length}</Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">events</Text>
                </View>
              }
            />
            {obsLoading ? (
              <ActivityIndicator color={colors.primary[600]} />
            ) : flowEvents.length === 0 ? (
              <NoteCard>
                <Text style={{ color: colors.text.tertiary }} className="text-sm">
                  No proof events recorded yet. They persist here as documents are indexed, DD chats
                  run, and submissions move through the claim flow.
                </Text>
              </NoteCard>
            ) : (
              <Panel>
                {flowEvents.map((f, i) => (
                  <Row key={`${f.createdAt}-${i}`} first={i === 0}>
                    <View className="flex-row items-center justify-between gap-2">
                      <View className="flex-1">
                        <Text style={{ color: colors.text.primary, fontFamily: 'monospace' }} className="text-xs" numberOfLines={1}>{f.eventName}</Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={1}>{f.ref ?? '—'} · {timeAgo(f.createdAt)}</Text>
                      </View>
                      <CircleDot size={13} color={colors.primary[600]} />
                    </View>
                  </Row>
                ))}
              </Panel>
            )}
          </Animated.View>

          {/* 5 — Certification Proof Events (real per-event counts) */}
          <Animated.View entering={FadeInDown.duration(400)}>
            <SectionHeaderRow
              icon={<ShieldCheck size={16} color={colors.primary[600]} />}
              title="Certification Proof Events"
              source="Per-event instrumentation. 6 persist to ai_observability_events (real counts); guard:cert enforces presence + payload fields in CI."
              right={
                <View className="flex-row items-center gap-1.5">
                  <CheckCircle2 size={16} color={colors.success} />
                  <Text style={{ color: colors.success }} className="text-sm font-bold">8/8</Text>
                </View>
              }
            />
            <Panel>
              {PROOF_EVENTS.map((p, i) => {
                const stat = proofStats[p.event];
                const seen = p.source === 'table' && stat && stat.count > 0;
                const chip =
                  p.source === 'table'
                    ? seen
                      ? { status: 'healthy' as const, label: `Seen ×${stat.count}` }
                      : { status: 'operator-only' as const, label: 'Awaiting' }
                    : p.source === 'analytics'
                    ? { status: 'healthy' as const, label: 'Queryable' }
                    : { status: 'operator-only' as const, label: 'Log-based' };
                return (
                  <Row key={p.event} first={i === 0}>
                    <View className="flex-row items-center justify-between gap-2">
                      <View className="flex-1">
                        <Text style={{ color: colors.text.primary, fontFamily: 'monospace' }} className="text-xs" numberOfLines={1}>
                          {p.event}
                        </Text>
                        <Text style={{ color: colors.text.tertiary }} className="text-xs mt-0.5" numberOfLines={1}>
                          {seen ? `${p.where} · last ${timeAgo(stat.lastSeen!)}` : p.where}
                        </Text>
                      </View>
                      <ObservabilityStatusChip status={chip.status} label={chip.label} icon={CircleDot} />
                    </View>
                  </Row>
                );
              })}
            </Panel>
            <View className="mt-2">
              <NoteCard>
                <Text style={{ color: colors.text.tertiary }} className="text-xs">
                  Where to look: ai_observability_events · analytics_events · llm_usage_daily ·
                  scoring_evals · Supabase Edge Function logs · Sentry. CI proof: bun run guard:cert.
                </Text>
              </NoteCard>
            </View>
          </Animated.View>

          {/* 5 — Alerts & Gaps */}
          <Animated.View entering={FadeInDown.duration(400)}>
            <SectionHeaderRow
              icon={<Bell size={16} color={colors.primary[600]} />}
              title="Alerts & Gaps"
              source="Alert signal recomputed from llm_usage_daily. Authoritative channel: daily GitHub Actions cron → Supabase logs (no llm_usage_alerts table)."
              right={
                alertState.yesterdayHasData ? (
                  <ObservabilityStatusChip
                    status={alertState.alerts.length === 0 ? 'healthy' : 'warning'}
                    label={alertState.alerts.length === 0 ? '0 alerts' : `${alertState.alerts.length} alert${alertState.alerts.length > 1 ? 's' : ''}`}
                  />
                ) : undefined
              }
            />
            <Panel>
              {!alertState.yesterdayHasData ? (
                <Row first>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">No usage yesterday — nothing to evaluate.</Text>
                </Row>
              ) : alertState.alerts.length === 0 ? (
                <Row first>
                  <View className="flex-row items-center gap-2">
                    <ObservabilityStatusChip status="healthy" label="No alerts" />
                    <Text style={{ color: colors.text.tertiary }} className="text-xs flex-1" numberOfLines={1}>
                      cost / error-rate / latency within thresholds ({alertState.trailingDays}d trailing)
                    </Text>
                  </View>
                </Row>
              ) : (
                alertState.alerts.map((a, i) => (
                  <Row key={a.kind} first={i === 0}>
                    <View className="flex-row items-start gap-2">
                      <ObservabilityStatusChip status="warning" label={a.kind.replace('_', ' ')} />
                      <Text style={{ color: colors.text.secondary }} className="text-xs flex-1" numberOfLines={3}>{a.message}</Text>
                    </View>
                  </Row>
                ))
              )}
            </Panel>
            <View className="mt-2">
              <Panel>
                {[
                  { icon: Coins, label: 'Embedding cost observability', status: 'missing' as const, note: 'planned, not implemented (see next-PR plan)' },
                  { icon: Search, label: 'Live retrieval eval', status: 'operator-only' as const, note: 'operator-only · requires seeded startup' },
                  { icon: FlaskConical, label: 'RAG / extraction eval persistence', status: 'operator-only' as const, note: 'console-only, not persisted yet' },
                  { icon: Bell, label: 'Usage alert delivery', status: 'operator-only' as const, note: 'daily cron + Supabase logs (+ email)' },
                ].map((g, i) => (
                  <Row key={g.label} first={i === 0}>
                    <View className="flex-row items-center justify-between gap-2">
                      <View className="flex-row items-center gap-2 flex-1">
                        <g.icon size={14} color={colors.text.tertiary} />
                        <View className="flex-1">
                          <Text style={{ color: colors.text.primary }} className="text-sm" numberOfLines={1}>{g.label}</Text>
                          <Text style={{ color: colors.text.tertiary }} className="text-xs" numberOfLines={1}>{g.note}</Text>
                        </View>
                      </View>
                      <ObservabilityStatusChip status={g.status} />
                    </View>
                  </Row>
                ))}
              </Panel>
            </View>
          </Animated.View>

          {/* 6 — Operator Actions / How to verify */}
          <Animated.View entering={FadeInDown.duration(400)}>
            <SectionHeaderRow
              icon={<Terminal size={16} color={colors.primary[600]} />}
              title="How to verify"
              source="Copyable commands. Live evals need .env keys; retrieval also needs a seeded staging startup."
            />
            <Panel>
              {COMMANDS.map((c) => (
                <CopyCommand key={c.label} label={c.label} cmd={c.cmd} />
              ))}
            </Panel>
          </Animated.View>

          <View className="flex-row items-center gap-1.5 mt-4 px-1">
            <FileText size={12} color={colors.text.tertiary} />
            <Text style={{ color: colors.text.tertiary }} className="text-xs flex-1" numberOfLines={2}>
              Read-only view. No raw emails, document text, prompts, or model outputs are shown or stored here.
            </Text>
          </View>
        </ScrollView>
      </SafeAreaView>
    </AppLayout>
  );
}
